import { Box, Slider, Stack, type StackProps } from '@mui/material'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ProcessedActivity } from '../../../../api/models'
import {
  DEAD_SPACE_MAX_SECONDS,
  calculateTimeFromPosition,
  getCombinedActivitiesAdjustedDurationInSeconds,
  getDurationAdjustedForDeadSpaceSeconds,
  groupActivitiesForScrubber
} from './scrubberUtils'
import { ScrubberSegment } from './ScrubberSegment'

type ScrubberProps = StackProps & {
  /**
   * Current playback time in seconds
   */
  currentTimeInSeconds: number

  recordingStartTimestamp: number | undefined

  /**
   * Total duration of the video in seconds
   */
  durationInSeconds: number

  /**
   * Activities to segment the scrubber
   */
  processedActivities: ProcessedActivity[]

  /**
   * Callback when user seeks to a new position
   */
  onSeek: (timeInSeconds: number) => void
}

const durationNormalizationFactor = 200

const TIME_JUMP_THRESHOLD_SECONDS = 1.5

export const Scrubber: FC<ScrubberProps> = ({
  currentTimeInSeconds,
  recordingStartTimestamp,
  durationInSeconds,
  processedActivities,
  onSeek,
  sx,
  ...stackProps
}) => {
  // Track whether we're currently dragging
  const [isDragging, setIsDragging] = useState(false)

  // Track whether we've just seeked (to disable transitions temporarily).
  // Transitions are for natural progression, not when jumping around. Looks weird.
  const [justSeeked, setJustSeeked] = useState(false)

  // Track the pending "clear justSeeked" timer so we can cancel it on unmount or before scheduling
  // a new one — prevents leaked setState calls after the component is gone.
  const justSeekedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scheduleClearJustSeeked = useCallback(() => {
    if (justSeekedTimeoutRef.current !== null) {
      clearTimeout(justSeekedTimeoutRef.current)
    }
    justSeekedTimeoutRef.current = setTimeout(() => {
      justSeekedTimeoutRef.current = null
      setJustSeeked(false)
    }, 50) // 50ms is enough to prevent transition on seek
  }, [])

  useEffect(() => {
    return () => {
      if (justSeekedTimeoutRef.current !== null) {
        clearTimeout(justSeekedTimeoutRef.current)
        justSeekedTimeoutRef.current = null
      }
    }
  }, [])

  // Track previous time to detect external seeks (like from play icon clicks)
  const previousTimeRef = useRef(currentTimeInSeconds)

  // Calculate step size dynamically (using a smaller step for smoother slider interaction)
  const step = useMemo(() => {
    return durationInSeconds / (durationNormalizationFactor * 10)
  }, [durationInSeconds])

  // Use the current time directly
  const normalizedCurrentTime = currentTimeInSeconds

  // Track the displayed scrubber value separately from the actual playback position
  const [displayValue, setDisplayValue] = useState(normalizedCurrentTime)

  // Process activities to create segments and calculate total duration
  const { engagementActivities, combinedActivityAdjustedDurationInSeconds } = useMemo(() => {
    return groupActivitiesForScrubber(processedActivities)
  }, [processedActivities])

  // Use activity-based duration for proportional sizing, fall back to player duration if needed
  const effectiveDurationInSeconds = combinedActivityAdjustedDurationInSeconds > 0 ? combinedActivityAdjustedDurationInSeconds : durationInSeconds
  const adjustedActivityDurationInSeconds = getCombinedActivitiesAdjustedDurationInSeconds(engagementActivities)
  const scrubberTrackDurationInSeconds = adjustedActivityDurationInSeconds > 0 ? adjustedActivityDurationInSeconds : effectiveDurationInSeconds

  // Add mouse event handlers for dragging
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return

      // Find the scrubber segments container to calculate relative position
      const scrubberContainer = document.querySelector('[data-testid="scrubber-segments-container"]') as HTMLElement
      if (!scrubberContainer) return

      const rect = scrubberContainer.getBoundingClientRect()
      const clickX = e.clientX - rect.left
      const newTime = calculateTimeFromPosition(clickX, rect.width, engagementActivities, scrubberTrackDurationInSeconds)

      setDisplayValue(newTime)
    }

    const handleMouseUp = () => {
      if (isDragging) {
        // Set justSeeked flag and clear it after a brief delay
        setJustSeeked(true)
        onSeek(displayValue)
        setIsDragging(false)
        scheduleClearJustSeeked()
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, scrubberTrackDurationInSeconds, displayValue, onSeek, engagementActivities, scheduleClearJustSeeked])

  // Update display value when current time changes (but only if not dragging)
  useEffect(() => {
    if (!isDragging) {
      // Detect if this is an external seek (significant time jump)
      const timeDifference = Math.abs(currentTimeInSeconds - previousTimeRef.current)
      const userIsSeeking = timeDifference > TIME_JUMP_THRESHOLD_SECONDS // If time jumps more than 1.5 seconds, it's likely a seek

      if (userIsSeeking) {
        // Set justSeeked flag to disable animations
        // Previously suppressed `react-hooks/set-state-in-effect` here. Biome 1.9.4 has no equivalent rule, and this effect intentionally toggles transient seek UI state.
        setJustSeeked(true)
        scheduleClearJustSeeked()
      }

      // Update display value and previous time
      // Previously suppressed `react-hooks/set-state-in-effect` here. Biome 1.9.4 has no equivalent rule, and this effect intentionally synchronizes displayed scrubber state.
      setDisplayValue(normalizedCurrentTime)
      previousTimeRef.current = currentTimeInSeconds
    }
  }, [normalizedCurrentTime, isDragging, currentTimeInSeconds, scheduleClearJustSeeked])

  // Handle when user starts dragging the slider
  const handleDragStart = () => {
    setIsDragging(true)
  }

  // Handle slider value change during drag
  const handleChange = (_event: Event, newValue: number | number[]) => {
    setDisplayValue(newValue as number)
  }

  // Handle when user releases the slider
  const handleDragEnd = useCallback(
    (_event: Event | React.SyntheticEvent<Element, Event>, newValue: number | number[]) => {
      const targetTime = newValue as number
      onSeek(targetTime)
      setIsDragging(false)
    },
    [onSeek]
  )

  return (
    <Stack
      data-testid="scrubber-segments-container"
      data-test-total-duration-seconds={scrubberTrackDurationInSeconds}
      direction={'row'}
      sx={{
        ...{
          width: '100%',
          alignItems: 'center',
          marginTop: 'var(--spacing-l)',
          height: 'var(--spacing-l)',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'visible'
        },
        ...sx
      }}
      {...stackProps}
      onClick={e => {
        // Calculate click position and seek to that time
        const rect = e.currentTarget.getBoundingClientRect()
        const clickX = e.clientX - rect.left
        const seekTime = calculateTimeFromPosition(clickX, rect.width, engagementActivities, scrubberTrackDurationInSeconds)

        // Set justSeeked flag and clear it after a brief delay
        setJustSeeked(true)
        onSeek(seekTime)
        scheduleClearJustSeeked()
      }}
    >
      {engagementActivities.length > 0 &&
        engagementActivities.map((segment, index) => {
          // Use flex-grow so segments fill the full width. Segments with no video use a small,
          // fixed max duration here so a long dead-space span doesn't dwarf its siblings.
          const percentageOfTotalTime = getDurationAdjustedForDeadSpaceSeconds(segment) / scrubberTrackDurationInSeconds

          const isCurrentSegment = currentTimeInSeconds >= segment.relativeStartTimeSeconds && currentTimeInSeconds <= segment.relativeEndTimeSeconds
          const isPlayedSegment = currentTimeInSeconds > segment.relativeEndTimeSeconds

          // Calculate how much of this segment is viewed
          let viewedPercent = 0
          if (isPlayedSegment) {
            viewedPercent = 1
          } else if (isCurrentSegment) {
            const progressInSegment = currentTimeInSeconds - segment.relativeStartTimeSeconds
            viewedPercent = progressInSegment / segment.durationSeconds
          }

          return (
            <ScrubberSegment
              key={segment.id}
              activityType={segment.type}
              index={index}
              durationSeconds={segment.durationSeconds}
              relativeStartTimeSeconds={segment.relativeStartTimeSeconds}
              percentageOfTotalTime={percentageOfTotalTime}
              viewedPercent={viewedPercent}
              isPlayedSegment={isPlayedSegment}
              isPrerollSegment={false}
              isPrimarilyDeadSpace={segment.isPrimarilyDeadSpace}
              isDragging={isDragging}
              justSeeked={justSeeked}
              showDivider={index < engagementActivities.length - 1}
            />
          )
        })}

      {/* Main scrubber ball positioned to align with segment fill end */}
      {engagementActivities.length > 0 &&
        (() => {
          // Use displayValue when dragging, currentTimeInSeconds when not dragging
          const timeForPosition = isDragging ? displayValue : currentTimeInSeconds

          // With flexbox layout, calculate ball position based on time progression
          // Each segment takes up space proportional to its duration

          let ballPositionPercent = 0

          for (let i = 0; i < engagementActivities.length; i++) {
            const segment = engagementActivities[i]
            const isCurrentSegment = timeForPosition >= segment.relativeStartTimeSeconds && timeForPosition <= segment.relativeEndTimeSeconds
            const segmentDurationRatio = getDurationAdjustedForDeadSpaceSeconds(segment) / scrubberTrackDurationInSeconds

            if (timeForPosition <= segment.relativeStartTimeSeconds) {
              // Haven't reached this segment yet
              break
            } else if (isCurrentSegment) {
              // Currently in this segment - calculate partial progress
              const progressInSegment = timeForPosition - segment.relativeStartTimeSeconds
              const segmentProgressRatio = segment.isPrimarilyDeadSpace
                ? // Primarily-dead-space segments only: measure progress against the fixed max duration (not the real duration)
                  Math.min(1, progressInSegment / DEAD_SPACE_MAX_SECONDS)
                : progressInSegment / segment.durationSeconds
              ballPositionPercent += segmentProgressRatio * segmentDurationRatio
              break
            } else {
              // Completed this segment
              ballPositionPercent += segmentDurationRatio
            }
          }

          // Convert to percentage (0-100%)
          const ballPositionPercentage = ballPositionPercent * 100
          return (
            <Box
              data-testid="scrubber-ball"
              sx={{
                position: 'absolute',
                left: `${ballPositionPercentage}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'var(--icons-xs)',
                height: 'var(--icons-xs)',
                backgroundColor: 'var(--bg-surface-warning)',
                border: '0.125rem solid var(--yellow-500-t40)',
                borderRadius: '50%',
                boxShadow: '0rem 0.125rem var(--spacing-3xs) rgba(0, 0, 0, 0.2)',
                zIndex: 30,
                cursor: isDragging ? 'grabbing' : 'grab',
                transition: isDragging || justSeeked ? 'none' : 'left 120ms linear',
                '&:active': { cursor: 'grabbing' },
                '&:hover': { boxShadow: '0rem 0rem 0rem var(--spacing-2xs) var(--orange-500-t18)' }
              }}
              onMouseDown={e => {
                setIsDragging(true)
                e.preventDefault()
              }}
            />
          )
        })()}

      {engagementActivities.length === 0 ? (
        // Fallback to original slider when no segments
        <Slider
          value={displayValue}
          min={0}
          max={durationInSeconds}
          step={step}
          onChange={handleChange}
          onMouseDown={handleDragStart}
          onChangeCommitted={handleDragEnd}
          valueLabelDisplay="auto"
          valueLabelFormat={value =>
            `${Math.floor(value / 60)}:${Math.floor(value % 60)
              .toString()
              .padStart(2, '0')}`
          }
          sx={{
            color: 'var(--yellow-500)',
            height: 'var(--spacing-2xs)',
            width: '100%',
            '& .MuiSlider-track': {
              border: 'none',
              height: 'var(--spacing-2xs)',
              background: 'var(--bg-surface-warning)'
            },
            '& .MuiSlider-rail': {
              height: 'var(--spacing-2xs)',
              opacity: 1,
              backgroundColor: 'var(--shape-action-transparent)'
            },
            '& .MuiSlider-thumb': {
              height: 'var(--icons-xs)',
              width: 'var(--icons-xs)',
              backgroundColor: 'var(--yellow-500)',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0rem 0rem 0rem var(--spacing-2xs) var(--orange-500-t18)'
              },
              transition: 'none'
            }
          }}
        />
      ) : null}
    </Stack>
  )
}
